import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  WhatsApp,
  LocationOn,
  Storefront,
  Phone,
  Person,
} from '@mui/icons-material';

import { usePagination } from '@frontend/features/common/components/Table';
import { CreateClientModal } from '../../features/clients/components/CreateClient';
import { EditClientModal } from '../../features/clients/components/EditClient';
import { useGetAllClients } from '../../features/clients/queries';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import { Link } from '@frontend/features/common/components/Link';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { getArrayParam, getTimeSinceDate } from '@frontend/features/common/helper';
import { routes } from '../routes';
import { ClientResponseDto } from '@bandido/shared/dtos';
import { SortingOptions } from '@frontend/features/common/types';
import { OrderOptionsValues } from '@bandido/shared/dtos';
import { ClientListFiltersOptions } from '../../features/clients/components/ClientListFiltersOptions';
import { useClientSessionFilters } from '../../features/clients/hooks/useClientSessionFilters';
import { useShowMineFilter } from '@frontend/features/common/hooks/useShowMineFilter';

export const ClientsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const pagination = usePagination();
  const navigate = useNavigate();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [editId, setEditId] = useState<string | undefined>();
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);

  const sort = searchParams.get('sort') || 'firstName';
  const order = searchParams.get('order') || 'ASC';

  const { updateFilters, clearFilters } = useClientSessionFilters(searchParams, setSearchParams);

  const showMineFilter = useShowMineFilter({
    filterField: 'preferredAgentIds',
    label: t('filters.myClients'),
    tooltip: t('filters.myClients'),
    searchParams,
    updateFilters,
  });

  const filters = {
    search,
    clientName: searchParams.get('clientName') || '',
    organizationIds: getArrayParam(searchParams.get('organizationIds'))?.map(Number),
    agencyIds: getArrayParam(searchParams.get('agencyIds'))?.map(Number),
    countries: getArrayParam(searchParams.get('countries')),
    cities: getArrayParam(searchParams.get('cities')),
    states: getArrayParam(searchParams.get('states')),
    documentTypes: getArrayParam(searchParams.get('documentTypes')),
    preferredAgentIds: getArrayParam(searchParams.get('preferredAgentIds')),
    sort,
    order: order as OrderOptionsValues,
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
  };

  const { data: clients, isLoading } = useGetAllClients(filters);

  const handleCloseCreateClientModal = () => {
    setOpenCreateModal(false);
  };

  const handleCloseEditModal = () => {
    setEditId(undefined);
    setOpenEditModal(false);
  };

  const handleClickClient = (id: string) => {
    navigate(routes.dashboard.clientView.replace(':id', id));
  };

  const handleUpdateSort = (sortOptions: SortingOptions) => {
    updateFilters({ sort: sortOptions.sort, order: sortOptions.order });
  };

  const handleCreateClient = () => {
    setOpenCreateModal(true);
  };

  const handleOnSearch = (value: string) => {
    setSearch(value);
    updateFilters({ search: value });
  };

  const handleClearFilters = () => {
    showMineFilter.clearFilter();
    clearFilters();
  };

  const formattedData = clients?.items.map((client: ClientResponseDto) => ({
    id: client.id,
    client: {
      id: client.id,
      name: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
      email: client.email || '',
    },
    agency: client.agencyName || '-',
    organization: client.organizationName || '-',
    phone: client.phoneCountryCode && client.phoneNumber 
      ? `+${client.phoneCountryCode} ${client.phoneNumber}` 
      : '-',
    whatsapp: client.whatsappNumber || '-',
    whatsappAi: client.whatsappWithAi,
    location: {
      country: client.country || '',
      city: client.city || '',
      state: client.state || '',
    },
    document: client.documentType && client.documentNumber 
      ? `${client.documentType}: ${client.documentNumber}`
      : '-',
    preferredAgent: {
      id: client.preferredAgentId,
      firstName: client.preferredAgentFirstName,
      lastName: client.preferredAgentLastName,
    },
    createdAt: client.createdAt,
    updatedAt: client.updatedAt,
  })) || [];

  const totalFilters = useMemo(() => {
    const keysToOmit = ['sort', 'order', 'search', 'page'];
    let count = 0;

    searchParams.forEach((value, key) => {
      if (!keysToOmit.includes(key)) {
        const values = value
          .split('|')
          .filter(Boolean)
          .filter((opt) => opt !== 'null' && opt !== null);
        count += values.length;
      }
    });

    return count > 0 ? count : undefined;
  }, [searchParams]);

  const urlPage = searchParams.get('page') || '1';
  
  useEffect(() => {
    pagination.handlePagination(Number(urlPage), true);
  }, [searchParams, urlPage]);

  const filtersOptions = {
    form: (
      <ClientListFiltersOptions
        title="Filtros"
        searchParams={searchParams}
        updateFilters={updateFilters}
      />
    ),
    filterListKey: 'clientslist',
    searchParams,
    setSearchParams,
    onClearFilters: handleClearFilters,
  };

  return (
    <ListLayout title={t('clients.title')} subtitle={t('clients.subtitle')}>
      <ListContentLayout
        pagination={pagination}
        filtersCount={totalFilters}
        stickyHeader
        filters={filtersOptions}
        onSearchChange={handleOnSearch}
        sorting={{
          options: { sort, order: order as OrderOptionsValues },
          handler: handleUpdateSort,
        }}
        onCreate={handleCreateClient}
        isLoading={isLoading}
        actionToggles={showMineFilter.actionToggle ? [showMineFilter.actionToggle] : undefined}
        data={clients ? {
          key: 'id',
          items: formattedData,
          totalCount: clients.count,
        } : undefined}
        columns={[
          {
            id: 'client',
            label: t('clients.table.client'),
            align: 'left',
            canSort: true,
            sortField: 'firstName',
            isSticky: true,
            minWidth: 150,
            maxWidth: 180,
            format: (value: { id: string; name: string; email: string }) => (
              <Stack
                sx={{ cursor: 'pointer' }}
                onClick={() => handleClickClient(value.id)}
                spacing={0.5}
              >
                <Tooltip title={value.name}>
                  <Typography 
                    variant="body2" 
                    color="primary" 
                    fontWeight="medium"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '160px'
                    }}
                  >
                    {value.name}
                  </Typography>
                </Tooltip>
                <Tooltip title={value.email}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '160px'
                    }}
                  >
                    {value.email}
                  </Typography>
                </Tooltip>
              </Stack>
            ),
          },
          {
            id: 'phone',
            label: t('clients.table.contact'),
            align: 'left',
            minWidth: 180,
            format: (_value: string, row: any) => (
              <Stack spacing={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Phone fontSize="small" color="disabled" />
                <Typography variant="body2">{row.phone}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <WhatsApp fontSize="small" color={row.whatsapp !== '-' ? 'success' : 'disabled'} />
                  <Typography variant="caption" color="text.secondary">
                    {row.whatsapp}
                  </Typography>
                </Box>
              </Stack>
            ),
          },
          {
            id: 'preferredAgent',
            label: t('clients.table.preferredAgent'),
            align: 'left',
            minWidth: 150,
            format: (value: { id?: string; firstName?: string; lastName?: string }) => (
              value.id && value.firstName && value.lastName ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <IconAvatar>
                    <Person fontSize="small" htmlColor={theme.palette.common.white} />
                  </IconAvatar>
                  <Link underline="hover" to={routes.dashboard.userView.replace(':id', String(value.id))}>
                    {`${value.firstName} ${value.lastName}`}
                  </Link>
                </Box>
              ) : (
                <Typography variant="body2">-</Typography>
              )
            ),
          },
          {
            id: 'organization',
            label: t('clients.table.organization'),
            align: 'left',
            canSort: true,
            sortField: 'organizationName',
            minWidth: 120,
            format: (value: string) => (
              <Typography variant="body2">{value}</Typography>
            ),
          },
          {
            id: 'agency',
            label: t('clients.table.agency'),
            align: 'left',
            canSort: true,
            sortField: 'agencyName',
            minWidth: 150,
            format: (value: string) => (
              <Box display="flex" alignItems="center" gap={1}>
                <Storefront fontSize="small" color="disabled" />
                <Typography variant="body2">{value}</Typography>
              </Box>
            ),
          },
          {
            id: 'location',
            label: t('clients.table.location'),
            align: 'left',
            canSort: true,
            sortField: 'country',
            minWidth: 150,
            format: (value: { country: string; city: string; state: string }) => (
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn fontSize="small" color="disabled" />
                <Stack spacing={0.25}>
                  <Typography variant="body2">
                    {[value.city, value.state].filter(Boolean).join(', ') || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {value.country || '-'}
                  </Typography>
                </Stack>
              </Box>
            ),
          },
          {
            id: 'createdAt',
            label: t('clients.table.created'),
            align: 'left',
            canSort: true,
            sortField: 'createdAt',
            minWidth: 100,
            format: (value: string) => (
              <Typography variant="caption" color="text.secondary">
                {getTimeSinceDate(value)}
              </Typography>
            ),
          },
        ]}
      />

      <CreateClientModal open={openCreateModal} onClose={handleCloseCreateClientModal} />
      <EditClientModal clientId={editId} open={openEditModal} onClose={handleCloseEditModal} />
    </ListLayout>
  );
};
